<template>
  <div class="subcategory-page">
    <div class="container">
      <!-- Breadcrumb -->
      <nav class="breadcrumb-inline">
        <a href="/">Home</a>
        <span>/</span>
        <a v-if="category?.slug" :href="`/${category.slug}`">{{ category?.name }}</a>
        <span v-else>Category</span>
        <span>/</span>
        <a v-if="category?.slug && subCategory?.slug" :href="`/${category.slug}/${subCategory.slug}`">{{ subCategory?.name || 'Subcategory' }}</a>
        <span v-else>{{ subCategory?.name || 'Subcategory' }}</span>
        <span>/</span>
        <span class="current">{{ subSubCategory?.name || 'Subsubcategory' }}</span>
      </nav>

      <!-- Subsubcategory Header -->
      <div class="subcategory-header-wrapper">
        <div class="subcategory-header">
          <h1>{{ subSubCategory?.name || 'Subsubcategory' }}</h1>
          <p v-if="subSubCategory?.description" class="subcategory-description">
            {{ subSubCategory?.description }}
          </p>
        </div>
      </div>

      <!-- Products Section -->
      <div class="products-wrapper">
        <div class="products-section">
          <div class="page-header">
            <p class="product-count">{{ products?.total || 0 }} products</p>
            <div class="sort-control">
              <label for="sort" class="sort-label">Sort:</label>
              <select id="sort" class="sort-select" v-model="selectedSort" @change="applySort">
                <option value="newest">Newest</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="name_asc">Name: A-Z</option>
                <option value="name_desc">Name: Z-A</option>
              </select>
            </div>
          </div>

          <!-- Products Grid -->
          <div v-if="products?.data && products.data.length > 0" class="products-grid">
            <product-card
              v-for="product in products.data"
              :key="product.id"
              :product="formatProduct(product)"
            />
          </div>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <i class="fas fa-box-open"></i>
            <h2>No Products Available</h2>
            <p>Check back later for new products in this subcategory</p>
            <a
              v-if="category?.slug && subCategory?.slug"
              :href="`/${category.slug}/${subCategory.slug}`"
              class="btn-back"
            >
              Back to {{ subCategory?.name || 'Subcategory' }}
            </a>
          </div>

          <!-- Pagination -->
          <div v-if="products?.data && products.data.length > 0 && products.last_page > 1" class="pagination">
            <a
              v-if="products.prev_page_url"
              :href="products.prev_page_url"
              class="page-btn"
            >
              <i class="fas fa-chevron-left"></i> Previous
            </a>

            <span class="page-info">
              Page {{ products.current_page }} of {{ products.last_page }}
            </span>

            <a
              v-if="products.next_page_url"
              :href="products.next_page_url"
              class="page-btn"
            >
              Next <i class="fas fa-chevron-right"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import ProductCard from '../../components/home/ProductCard.vue'

const props = defineProps({
  category: {
    type: Object,
    required: false,
    default: null,
  },
  subCategory: {
    type: Object,
    required: false,
    default: null,
  },
  subSubCategory: {
    type: Object,
    required: false,
    default: null,
  },
  products: {
    type: Object,
    required: false,
    default: null,
  },
  sort: {
    type: String,
    default: 'newest'
  }
})

const selectedSort = ref(props.sort || 'newest')

const applySort = () => {
  const url = new URL(window.location.href)
  url.searchParams.set('sort', selectedSort.value)
  url.searchParams.delete('page')
  window.location.href = url.toString()
}

const formatProduct = (product) => {
  // Consistent with Product model/migration: base_price = final price, compare_price = old crossed-out price
  const sellingPrice = product.base_price
  const hasDiscount = product.compare_price && product.compare_price > product.base_price
  const discount = hasDiscount
    ? Math.round(((product.compare_price - product.base_price) / product.compare_price) * 100)
    : null

  return {
    id: product.id,
    name: product.name,
    slug: product.slug,
    price: sellingPrice,
    original_price: hasDiscount ? product.compare_price : null,
    image: product.image || '/images/placeholder.jpg',
    discount: discount,
    rating: product.average_rating || 0,
    reviews: product.reviews_count || 0,
    stock_status: product.is_active ? 'in-stock' : 'out'
  }
}
</script>
